import { useState, useEffect } from 'react';
import { RoundProps } from './types';

function WrongWarning({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => { const t = setTimeout(onDismiss, 3000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-amber-500 text-white px-7 py-4 rounded-2xl text-base font-bold shadow-lg max-w-xs text-center animate-bounce">
      {message}
    </div>
  );
}

function Instruction({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-4 py-3 text-center w-full max-w-lg mx-auto">
      <p className="text-sm font-extrabold text-amber-800">{text}</p>
      {sub && <p className="text-xs text-amber-600 mt-1 italic">{sub}</p>}
    </div>
  );
}

// ── Magnifying glass UI component ──────────────────────────────────────
function MagnifyCard({
  label,
  baseIcon,
  magnifiedIcon,
  magnifiedText,
  onClick,
  revealed,
  selected,
  onHover,
}: {
  label: string;
  baseIcon: string;
  magnifiedIcon: string;
  magnifiedText: string;
  onClick: () => void;
  revealed: boolean;
  selected: boolean;
  onHover: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => {
    setHovered(true);
    onHover();
  };

  return (
    <div
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
      className={`relative flex flex-col items-center gap-2 p-3 rounded-2xl border-2 cursor-pointer transition-all duration-200 select-none
        ${selected ? 'border-green-400 bg-green-50 scale-105' : hovered ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white hover:scale-105'}
      `}
      style={{ minWidth: 120 }}>
      {/* Base icon */}
      <span className="text-4xl">{baseIcon}</span>
      <p className="text-xs font-extrabold text-gray-600 text-center leading-tight">{label}</p>

      {/* Magnifying glass badge */}
      <div className={`absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded-full transition-all
        ${hovered ? 'bg-amber-400 scale-125' : 'bg-amber-100'}`}>
        <i className="ri-search-line text-amber-700 text-xs"></i>
      </div>

      {/* Magnified detail — show on hover */}
      {hovered && (
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 z-30 bg-white border-2 border-amber-400 rounded-2xl px-3 py-2 shadow-lg w-44 text-center pointer-events-none"
          style={{ animation: 'none' }}>
          <div className="text-2xl mb-1">{revealed ? magnifiedIcon : '🔍'}</div>
          {revealed
            ? <p className="text-xs font-bold text-gray-700 leading-tight">{magnifiedText}</p>
            : <p className="text-xs text-amber-600 italic">Đang soi kính lúp...</p>
          }
          <div className="absolute bottom-[-7px] left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-r-2 border-b-2 border-amber-400 rotate-45"></div>
        </div>
      )}
    </div>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────

const RICE_BAGS = [
  {
    id: 0, label: 'Túi gạo #1', baseIcon: '🛍️', status: 'clean' as const,
    magnifiedIcon: '✨', magnifiedText: 'Hạt trắng đều, không mọt, không mốc — sạch và thơm!',
  },
  {
    id: 1, label: 'Túi gạo #2', baseIcon: '🛍️', status: 'moldy' as const,
    magnifiedIcon: '🦠', magnifiedText: 'Có đốm đen, nấm mốc trắng đang lan ra — BỊ MỐC!',
  },
  {
    id: 2, label: 'Túi gạo #3', baseIcon: '🛍️', status: 'buggy' as const,
    magnifiedIcon: '🐛', magnifiedText: 'Thấy con mọt nhỏ đang bò trong gạo — CÓ MỌT!',
  },
  {
    id: 3, label: 'Túi gạo #4', baseIcon: '🛍️', status: 'wrong_type' as const,
    magnifiedIcon: '❓', magnifiedText: 'Gạo tẻ thường, không phải gạo nếp để làm cơm lam!',
  },
];

const WATER_JUGS = [
  {
    id: 0, label: 'Ca nước #1', baseIcon: '🫙', ok: true,
    magnifiedIcon: '✨', magnifiedText: 'Trong vắt, không tạp chất — an toàn để dùng!',
  },
  {
    id: 1, label: 'Ca nước #2', baseIcon: '🫙', ok: false,
    magnifiedIcon: '🦠', magnifiedText: 'Thấy bụi nhỏ và vi khuẩn đang bơi — KHÔNG DÙNG!',
  },
  {
    id: 2, label: 'Ca nước #3', baseIcon: '🫙', ok: false,
    magnifiedIcon: '🧫', magnifiedText: 'Tảo xanh và cặn bẩn tan trong nước — ô nhiễm!',
  },
  {
    id: 3, label: 'Ca nước #4', baseIcon: '🫙', ok: false,
    magnifiedIcon: '🔴', magnifiedText: 'Vi khuẩn đỏ và cặn đen — nguy hiểm, không dùng!',
  },
];

const BAMBOO_TUBES = [
  {
    id: 0, label: 'Ống tre #1', baseIcon: '🎋', ok: false,
    magnifiedIcon: '💔', magnifiedText: 'Có vết nứt nhỏ dọc thân — cơm sẽ trào ra khi nướng!',
  },
  {
    id: 1, label: 'Ống tre #2', baseIcon: '🎋', ok: false,
    magnifiedIcon: '🍂', magnifiedText: 'Thành ống quá dày, già quá — cơm sẽ có mùi đắng!',
  },
  {
    id: 2, label: 'Ống tre #3', baseIcon: '🎋', ok: true,
    magnifiedIcon: '✅', magnifiedText: 'Thành ống đều, vừa đủ tuổi, bên trong sạch — hoàn hảo!',
  },
  {
    id: 3, label: 'Ống tre #4', baseIcon: '🎋', ok: false,
    magnifiedIcon: '🌱', magnifiedText: 'Ống còn non, thành mỏng — dễ vỡ khi nướng!',
  },
];

// ── Main Component ────────────────────────────────────────────────────────
export function Round1ChonNguyenLieu({ onComplete }: RoundProps) {
  const [subStep, setSubStep] = useState(0);
  const [revealedIds, setRevealedIds] = useState<Set<number>>(new Set());
  const [wrong, setWrong] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [leafWashed, setLeafWashed] = useState(false);
  const [draggingLeaf, setDraggingLeaf] = useState(false);

  const SUBSTEPS = ['Chọn gạo nếp', 'Chọn nước', 'Chọn ống tre', 'Rửa lá chuối'];

  const revealAfterHover = (id: number) => {
    setRevealedIds(prev => new Set([...prev, id]));
  };

  // ── Sub-step 0: Choose rice ────────────────────────────────────────────
  const handleRiceClick = (bag: typeof RICE_BAGS[0]) => {
    if (!revealedIds.has(bag.id)) {
      setWrong('🔍 Hãy di chuột vào túi để soi kính lúp trước khi chọn nhé!');
      return;
    }
    if (bag.status !== 'clean') {
      setWrong('💡 Ờm... Kính lúp cho thấy gì rồi nhỉ? Hãy soi thêm các túi khác để so sánh nhé!');
      return;
    }
    setSelectedId(bag.id);
    setTimeout(() => { setSubStep(1); setRevealedIds(new Set()); setSelectedId(null); }, 700);
  };

  // ── Sub-step 1: Choose water ──────────────────────────────────────────
  const handleWaterClick = (jug: typeof WATER_JUGS[0]) => {
    if (!revealedIds.has(jug.id)) {
      setWrong('🔍 Hãy di chuột vào ca để soi kính lúp trước khi chọn nhé!');
      return;
    }
    if (!jug.ok) {
      setWrong('💡 Kính lúp đã thấy gì đó lạ lạ rồi đấy! Thử soi ca khác xem sao nhé!');
      return;
    }
    setSelectedId(jug.id);
    setTimeout(() => { setSubStep(2); setRevealedIds(new Set()); setSelectedId(null); }, 700);
  };

  // ── Sub-step 2: Choose bamboo ─────────────────────────────────────────
  const handleBambooClick = (tube: typeof BAMBOO_TUBES[0]) => {
    if (!revealedIds.has(tube.id)) {
      setWrong('🔍 Hãy di chuột vào ống để soi kính lúp trước khi chọn nhé!');
      return;
    }
    if (!tube.ok) {
      setWrong('💡 Kính lúp đã thấy vấn đề rồi! Thử kiểm tra ống khác xem có tốt hơn không nhé!');
      return;
    }
    setSelectedId(tube.id);
    setTimeout(() => { setSubStep(3); setRevealedIds(new Set()); setSelectedId(null); }, 700);
  };

  return (
    <div className="flex flex-col items-center gap-3 p-4 bg-emerald-50 rounded-3xl">
      {wrong && <WrongWarning message={wrong} onDismiss={() => setWrong('')} />}

      <h3 className="text-xl font-extrabold text-emerald-700 text-center">🛒 Chọn nguyên liệu!</h3>

      {/* Sub-step tabs */}
      <div className="flex gap-1 bg-white rounded-2xl p-1 border border-emerald-200 flex-wrap justify-center w-full max-w-lg">
        {SUBSTEPS.map((s, i) => (
          <div key={i} className={`px-2 py-1.5 rounded-xl text-xs font-extrabold whitespace-nowrap transition-all ${
            i === subStep ? 'bg-emerald-500 text-white' : i < subStep ? 'bg-green-100 text-green-600' : 'text-gray-400'
          }`}>
            {i < subStep ? '✅' : `${i + 1}.`} {s}
          </div>
        ))}
      </div>

      {/* Magnifying glass instruction banner */}
      <div className="flex items-center gap-2 bg-amber-100 border border-amber-300 rounded-2xl px-4 py-2 w-full max-w-lg">
        <span className="text-xl">🔍</span>
        <p className="text-xs font-bold text-amber-800">
          Di chuột vào từng vật để soi kính lúp — kính sẽ tiết lộ bí mật bên trong! Sau đó mới nhấp để chọn.
        </p>
      </div>

      {/* Sub-step 0: Rice */}
      {subStep === 0 && (
        <div className="flex flex-col items-center gap-3 w-full">
          <Instruction text="🍚 Chọn túi gạo nếp sạch để làm cơm lam!" sub="Soi kính lúp để xem bên trong từng túi rồi mới chọn" />
          <div className="grid grid-cols-4 gap-3 w-full max-w-lg">
            {RICE_BAGS.map(bag => (
              <MagnifyCard
                key={bag.id}
                label={bag.label}
                baseIcon={bag.baseIcon}
                magnifiedIcon={bag.magnifiedIcon}
                magnifiedText={bag.magnifiedText}
                onClick={() => handleRiceClick(bag)}
                revealed={revealedIds.has(bag.id)}
                selected={selectedId === bag.id}
                onHover={() => revealAfterHover(bag.id)}
              />
            ))}
          </div>
          <p className="text-xs text-emerald-600 font-bold">👆 Di chuột vào từng túi → soi xong → nhấp để chọn!</p>
        </div>
      )}

      {/* Sub-step 1: Water */}
      {subStep === 1 && (
        <div className="flex flex-col items-center gap-3 w-full">
          <Instruction text="💧 Chọn ca nước sạch để dùng!" sub="Bốn ca nhìn giống nhau — phải soi kính lúp mới biết cái nào sạch" />
          <div className="grid grid-cols-4 gap-3 w-full max-w-lg">
            {WATER_JUGS.map(jug => (
              <MagnifyCard
                key={jug.id}
                label={jug.label}
                baseIcon={jug.baseIcon}
                magnifiedIcon={jug.magnifiedIcon}
                magnifiedText={jug.magnifiedText}
                onClick={() => handleWaterClick(jug)}
                revealed={revealedIds.has(jug.id)}
                selected={selectedId === jug.id}
                onHover={() => revealAfterHover(jug.id)}
              />
            ))}
          </div>
          <p className="text-xs text-sky-600 font-bold">🔍 Soi kính lúp từng ca nước rồi chọn ca trong sạch nhất!</p>
        </div>
      )}

      {/* Sub-step 2: Bamboo */}
      {subStep === 2 && (
        <div className="flex flex-col items-center gap-3 w-full">
          <Instruction text="🎋 Chọn ống tre phù hợp để nướng cơm lam!" sub="Bốn ống nhìn giống nhau từ ngoài — kính lúp sẽ lộ điểm khác biệt" />
          <div className="grid grid-cols-4 gap-3 w-full max-w-lg">
            {BAMBOO_TUBES.map(tube => (
              <MagnifyCard
                key={tube.id}
                label={tube.label}
                baseIcon={tube.baseIcon}
                magnifiedIcon={tube.magnifiedIcon}
                magnifiedText={tube.magnifiedText}
                onClick={() => handleBambooClick(tube)}
                revealed={revealedIds.has(tube.id)}
                selected={selectedId === tube.id}
                onHover={() => revealAfterHover(tube.id)}
              />
            ))}
          </div>
          <p className="text-xs text-emerald-600 font-bold">🎋 Soi kính lúp rồi chọn ống không bị nứt, vừa đủ tuổi!</p>
        </div>
      )}

      {/* Sub-step 3: Wash leaf */}
      {subStep === 3 && (
        <div className="flex flex-col items-center gap-4 w-full">
          <Instruction text="🍃 Kéo lá chuối vào chậu nước để rửa sạch!" sub="Lá bẩn có vi khuẩn! Phải rửa sạch mới được dùng bịt miệng ống" />
          <div className="flex items-center gap-8 justify-center w-full mt-2">
            <div
              draggable={!leafWashed}
              onDragStart={() => setDraggingLeaf(true)}
              onDragEnd={() => setDraggingLeaf(false)}
              className={`flex flex-col items-center gap-2 select-none transition-all ${
                leafWashed ? 'opacity-40' : 'cursor-grab active:cursor-grabbing hover:scale-110 animate-bounce'
              }`}>
              <span className="text-6xl">🍃</span>
              <span className="text-sm font-extrabold text-green-700">Kéo lá →</span>
            </div>
            <span className="text-3xl text-emerald-400 font-bold">→→</span>
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault(); setDraggingLeaf(false); setLeafWashed(true);
                setTimeout(() => onComplete(true, 90), 600);
              }}
              className={`flex flex-col items-center justify-center w-28 h-24 rounded-2xl border-4 border-dashed transition-all ${
                draggingLeaf ? 'border-sky-500 bg-sky-100 scale-110' : 'border-sky-300 bg-sky-50'
              }`}>
              <span className="text-4xl">{leafWashed ? '✅' : '🪣'}</span>
              <span className="text-xs font-extrabold text-sky-600 text-center">{leafWashed ? 'Đã rửa sạch!' : 'Thả lá vào chậu'}</span>
            </div>
          </div>
          {!leafWashed && (
            <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl px-4 py-3 text-sm text-orange-700 font-bold text-center max-w-md">
              ⚠️ Nếu dùng lá chưa rửa, vi khuẩn sẽ nhiễm vào cơm lam!
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Food Classification (used in KnowledgeTest) ─────────────────────────────
export function Round2PhanLoaiThucPham({ onComplete }: RoundProps) {
  const [phase, setPhase] = useState<'intro' | 'play' | 'done'>('intro');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState('');
  const [showFeedback, setShowFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [correctAnswerText, setCorrectAnswerText] = useState('');
  const [timeLeft, setTimeLeft] = useState(20);

  const FOOD_ITEMS = [
    { id: 0, emoji: '🥕', label: 'Cà rốt tươi mới rửa', clean: true },
    { id: 1, emoji: '🍎', label: 'Táo có sâu đục', clean: false },
    { id: 2, emoji: '🍚', label: 'Gạo nếp trắng', clean: true },
    { id: 3, emoji: '🥛', label: 'Sữa quá hạn sử dụng', clean: false },
    { id: 4, emoji: '🥬', label: 'Rau mới hái', clean: true },
    { id: 5, emoji: '🍚', label: 'Cơm thiu để qua đêm', clean: false },
    { id: 6, emoji: '🥚', label: 'Trứng tươi không nứt', clean: true },
    { id: 7, emoji: '🍗', label: 'Thịt gà có mùi hôi', clean: false },
    { id: 8, emoji: '🍌', label: 'Chuối chín vàng tươi', clean: true },
    { id: 9, emoji: '🧅', label: 'Hành tây bị thối mềm', clean: false },
  ];

  const shuffled = FOOD_ITEMS;

  const handleChoice = (isClean: boolean) => {
    const item = shuffled[currentIdx];
    const isCorrect = item.clean === isClean;
    if (isCorrect) {
      setCorrect(c => c + 1);
      setScore(s => s + 10);
      setShowFeedback('correct');
      setCorrectAnswerText('');
    } else {
      setShowFeedback('wrong');
      const correctText = item.clean ? `${item.emoji} "${item.label}" còn tốt — dùng được!` : `${item.emoji} "${item.label}" đã hỏng — không dùng!`;
      setCorrectAnswerText(correctText);
      setWrong(item.clean ? `${item.emoji} ${item.label} còn tốt! Nên dùng!` : `${item.emoji} ${item.label} đã hỏng! Không được dùng!`);
    }
    setTimeout(() => {
      setShowFeedback(null);
      setCorrectAnswerText('');
      const next = currentIdx + 1;
      if (next >= shuffled.length) setPhase('done');
      else { setCurrentIdx(next); setTimeLeft(20); }
    }, 1400);
  };

  if (phase === 'intro') return (
    <div className="flex flex-col items-center gap-4 p-5 bg-lime-50 rounded-3xl">
      <div className="text-5xl animate-bounce">🔍</div>
      <h3 className="text-xl font-extrabold text-lime-700 text-center">Phân loại thực phẩm!</h3>
      <p className="text-sm text-lime-600 text-center max-w-sm">Thực phẩm lần lượt xuất hiện. Em bấm <strong>Được dùng</strong> nếu còn tốt, bấm <strong>Không dùng</strong> nếu đã hỏng!</p>
      <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
        <div className="bg-green-100 border border-green-300 rounded-2xl p-3 text-center font-extrabold text-green-700 text-sm">✅ Được dùng</div>
        <div className="bg-red-100 border border-red-300 rounded-2xl p-3 text-center font-extrabold text-red-700 text-sm">❌ Không dùng</div>
      </div>
      <button onClick={() => setPhase('play')} className="px-8 py-3 bg-lime-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-lime-600 whitespace-nowrap">
        Bắt đầu nào! →
      </button>
    </div>
  );

  if (phase === 'done') {
    const pct = Math.round((correct / shuffled.length) * 100);
    return (
      <div className="flex flex-col items-center gap-4 p-5 bg-lime-50 rounded-3xl">
        <div className="text-5xl">{pct >= 80 ? '🌟' : pct >= 60 ? '😊' : '💪'}</div>
        <h3 className="text-xl font-extrabold text-lime-700">Kết quả phân loại!</h3>
        <div className="bg-white rounded-2xl p-4 text-center w-full max-w-sm border border-lime-200">
          <p className="text-3xl font-extrabold text-lime-600 mb-1">{correct}/{shuffled.length}</p>
          <p className="text-sm text-gray-600">câu đúng — {pct}% chính xác</p>
        </div>
        <button onClick={() => onComplete(true, score)} className="px-8 py-3 bg-lime-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-lime-600 whitespace-nowrap">
          Tiếp tục nấu cơm lam! →
        </button>
      </div>
    );
  }

  const item = shuffled[currentIdx];
  return (
    <div className="flex flex-col items-center gap-3 p-4 bg-lime-50 rounded-3xl min-h-64">
      {wrong && showFeedback === 'wrong' && <WrongWarning message={wrong} onDismiss={() => setWrong('')} />}
      <div className="flex justify-between w-full max-w-md items-center">
        <span className="text-xs font-extrabold text-lime-700">{currentIdx + 1}/{shuffled.length}</span>
        <div className="w-32 h-3 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-lime-500 rounded-full transition-all" style={{ width: `${(currentIdx / shuffled.length) * 100}%` }} />
        </div>
        <span className={`text-xs font-extrabold ${timeLeft <= 5 ? 'text-red-500 animate-pulse' : 'text-lime-700'}`}>⏱️ {timeLeft}s</span>
      </div>
      <div className={`flex flex-col items-center gap-3 w-full max-w-md p-5 rounded-3xl border-4 transition-all ${
        showFeedback === 'correct' ? 'bg-green-100 border-green-400' : showFeedback === 'wrong' ? 'bg-red-100 border-red-400' : 'bg-white border-lime-300'
      }`}>
        <span className="text-7xl">{item.emoji}</span>
        <p className="text-lg font-extrabold text-gray-800 text-center">{item.label}</p>
        {showFeedback === 'correct' && <p className="text-green-600 font-extrabold text-lg">✅ Đúng rồi!</p>}
        {showFeedback === 'wrong' && (
          <div className="flex flex-col items-center gap-2">
            <p className="text-red-600 font-extrabold text-lg">❌ Sai rồi!</p>
            <div className="bg-amber-50 border border-amber-300 rounded-xl px-3 py-2 text-xs font-bold text-amber-800 text-center">
              💡 Đáp án đúng: {correctAnswerText}
            </div>
          </div>
        )}
      </div>
      {!showFeedback && (
        <div className="flex gap-4 w-full max-w-md">
          <button onClick={() => handleChoice(true)} className="flex-1 py-4 bg-green-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-green-600 active:scale-95 transition-all whitespace-nowrap">✅ Được dùng</button>
          <button onClick={() => handleChoice(false)} className="flex-1 py-4 bg-red-500 text-white font-extrabold text-base rounded-2xl cursor-pointer hover:bg-red-600 active:scale-95 transition-all whitespace-nowrap">❌ Không dùng</button>
        </div>
      )}
    </div>
  );
}
